import { ROLES } from "@/types/permissions";
import {
  AcademicCapIcon,
  HomeIcon,
  InformationCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { lazy } from "react";
import { type NavigationItem, type RouteConfig } from "../types/router";

// Lazy load all page components
const LoginPage = lazy(() => import("../pages/auth/LoginPage"));

// Course author Pages
const SomsagtDashboard = lazy(() => import("../pages/somsagt/Dashboard"));
const SomsagtCourses = lazy(() => import("../pages/somsagt/Courses"));
const SomsagtStatistics = lazy(() => import("../pages/somsagt/Statistics"));
const SomsagtCreateCourse = lazy(() => import("../pages/somsagt/NewCourse"));

// Client manager Pages
const ClientManagerDashboard = lazy(
  () => import("../pages/client-manager/Dashboard"),
);
const ClientManagerClientTraineesPage = lazy(
  () => import("../pages/client-manager/ClientTrainees"),
);

// Trainee Pages
const ClientTraineeDashboard = lazy(
  () => import("../pages/client-trainee/Dashboard"),
);
const ClientTraineeCourses = lazy(
  () => import("../pages/client-trainee/Courses"),
);
const ClientTraineeCoursePage = lazy(
  () => import("../pages/client-trainee/CourseDetail"),
);
const ClientTraineeCourseModulePage = lazy(
  () => import("../pages/client-trainee/CourseMaterialDetail"),
);
const ClientTraineeCourseActivityPage = lazy(
  () => import("../pages/client-trainee/ModuleActivityDetail"),
);
const ClientTraineeSimulatorPage = lazy(
  () => import("../pages/client-trainee/SimulatorPage"),
);
const ClientTraineeProfile = lazy(
  () => import("../pages/client-trainee/Profile"),
);
const AboutCL = lazy(() => import("../pages/common/AboutCL"));
const ClientTraineeCourseMaterial = lazy(
  () => import("../pages/client-trainee/CourseMaterial"),
);
const ClientTraineeCourseMaterialDetail = lazy(
  () => import("../pages/client-trainee/CourseMaterialDetail"),
);

// Common Pages
const NotFound = lazy(() => import("../pages/common/NotFound"));
const Unauthorized = lazy(() => import("../pages/common/Unauthorized"));
const PostLoginTestPage = lazy(() => import("../pages/auth/CallbackPage"));
const SmartDashboard = lazy(() => import("../pages/common/SmartDashboard"));
const CourseAdministration = lazy(
  () => import("../pages/course-administration/CourseAdministration"),
);

// Route configurations
export const PROTECTED_ROUTES: RouteConfig[] = [
  // {
  //   path: "/somsagt/dashboard",
  //   component: SomsagtDashboard,
  //   roles: [
  //     ROLES.INTERNAL_ADMIN,
  //     ROLES.INTERNAL_CLIENT_MANAGER,
  //     ROLES.INTERNAL_COURSE_AUTHOR,
  //   ],
  //   title: "Dashboard",
  // },

  // Course routes
  {
    path: "/somsagt/courses",
    component: SomsagtCourses,
    roles: [ROLES.INTERNAL_ADMIN],
    title: "Courses",
  },
  {
    path: "/course-administration",
    component: CourseAdministration,
    roles: [ROLES.INTERNAL_ADMIN, ROLES.COURSE_AUTHOR],
    title: "Course Administration",
  },
  {
    path: "/somsagt/courses/new",
    component: SomsagtCreateCourse,
    roles: [ROLES.INTERNAL_ADMIN],
    title: "Create Course",
  },
  {
    path: "/simulator",
    component: ClientTraineeSimulatorPage,
    roles: [
      ROLES.CLIENT_TRAINEE,
      ROLES.INTERNAL_ADMIN,
      ROLES.CONVERSATION_REVIEWER,
      ROLES.USER_ADMIN,
    ],
    title: "AI Simulator",
  },
  {
    path: "/profile",
    component: ClientTraineeProfile,
    roles: [
      ROLES.CLIENT_TRAINEE,
      ROLES.CLIENT_MANAGER,
      ROLES.INTERNAL_ADMIN,
      ROLES.USER_ADMIN,
      ROLES.CONVERSATION_REVIEWER,
    ],
    title: "My profile",
  },
  {
    path: "/course-material",
    component: ClientTraineeCourseMaterial,
    roles: [
      ROLES.CLIENT_TRAINEE,
      ROLES.INTERNAL_ADMIN,
      ROLES.USER_ADMIN,
      ROLES.CONVERSATION_REVIEWER,
    ],
    title: "Course Material",
  },
  {
    path: "/course-material/:moduleId",
    component: ClientTraineeCourseMaterialDetail,
    roles: [
      ROLES.CLIENT_TRAINEE,
      ROLES.INTERNAL_ADMIN,
      ROLES.USER_ADMIN,
      ROLES.CONVERSATION_REVIEWER,
    ],
    title: "Course Material Detail",
  },
  {
    path: "/about",
    component: AboutCL,
    roles: [], // Public for all authenticated users
    title: "About CL",
  },
];

export const NAVIGATION: NavigationItem[] = [
  // {
  //   id: "somsagt-dashboard",
  //   title: "Dashboard",
  //   path: "/somsagt/dashboard",
  //   icon: HomeIcon,,
  //   roles: [
  //     ROLES.INTERNAL_ADMIN,
  //     ROLES.INTERNAL_CLIENT_MANAGER,
  //     ROLES.INTERNAL_COURSE_AUTHOR,
  //   ],
  // },
  {
    id: "simulator",
    title: "Simulator",
    path: "/simulator",
    icon: HomeIcon,
    roles: [
      ROLES.CLIENT_TRAINEE,
      ROLES.INTERNAL_ADMIN,
      ROLES.USER_ADMIN,
      ROLES.CONVERSATION_REVIEWER,
    ],
  },

  {
    id: "my-profile",
    title: "My Profile",
    path: "/profile",
    icon: UserIcon,
    roles: [
      ROLES.CLIENT_TRAINEE,
      ROLES.CLIENT_MANAGER,
      ROLES.INTERNAL_ADMIN,
      ROLES.USER_ADMIN,
      ROLES.CONVERSATION_REVIEWER,
    ],
  },
  {
    id: "course-material",
    title: "Course Material",
    path: "/course-material",
    icon: InformationCircleIcon,
    roles: [
      ROLES.CLIENT_TRAINEE,
      ROLES.INTERNAL_ADMIN,
      ROLES.USER_ADMIN,
      ROLES.CONVERSATION_REVIEWER,
    ],
  },
  {
    id: "course-administration",
    title: "Course Administration",
    path: "/course-administration",
    icon: AcademicCapIcon,
    roles: [ROLES.INTERNAL_ADMIN, ROLES.COURSE_AUTHOR],
  },
  {
    id: "about",
    title: "About CL",
    path: "/about",
    icon: InformationCircleIcon,
    roles: [], // Available to all authenticated users
  },
];

export const PUBLIC_ROUTES: RouteConfig[] = [
  {
    path: "/",
    component: LoginPage,
    roles: [],
    title: "Login",
  },
  {
    path: "/login",
    component: LoginPage,
    roles: [],
    title: "Login",
  },
  {
    path: "/unauthorized",
    component: Unauthorized,
    roles: [],
    title: "Unauthorized",
  },
  // {
  //   path: "*",
  //   component: NotFound,
  //   roles: [],
  //   title: "Not Found",
  // },
  {
    path: "/callback",
    component: PostLoginTestPage,
    roles: [],
    title: "Auth Callback",
  },
];
